/**
 * Issue #166, FAC-06 — training facilities on the client, and on the same
 * map as the grounds.
 *
 * A FACILITY IS NOT A PLACE-TYPED GROUND, and this module exists partly to
 * keep the two from collapsing into each other on screen. `grounds.place_type`
 * (#136) says what a PIN somebody dropped IS — a field, a nets facility, an
 * indoor centre — and it is one value per pin. `ground_facilities` says what
 * ONE VENUE IS MADE OF: "Torvale Club has three nets, one of them matting".
 * A standalone nets ground and a net inside a club are both real and both
 * legitimately appear a hundred metres apart; #132's duplicate detector
 * deliberately never compares across types for the same reason. So they must
 * never be drawn as though one were a copy of the other:
 *
 *   - a ground is a TEARDROP pin whose colour says whose ground it is and
 *     whose letter says what kind of place it is (`GroundsMap.jsx`);
 *   - a facility is a SMALLER, ROUND pin in one colour that no ground pin
 *     uses, whose letter is its SURFACE (T/S/M/C).
 *
 * Shape and colour are both different, so the two are separable at a glance
 * and at a squint, and the legend names both rather than leaving the
 * difference to be inferred.
 *
 * THE PIN IS OPTIONAL, AND AN UNPINNED FACILITY IS NOT DRAWN AT A GUESSED
 * POSITION. `ground_facilities.latitude/longitude` are nullable by design
 * (schema.ts: "the venue pin is approximate ... the surface pin is
 * authoritative and drives navigation"), so a facility nobody has stood in
 * front of with a phone has no position of its own. Offsetting it around its
 * venue would put a coordinate on the map that no row supports — for the one
 * feature whose entire purpose is telling somebody where to go. Unpinned
 * facilities are therefore reported ON their venue: in its popup, on its
 * card, and in the detail dialog. They are never invented onto the map.
 */

/**
 * FAC-03's settled surface vocabulary (#165), character for character the
 * API's `FACILITY_SURFACES` and the `ground_facilities_surface_check` CHECK
 * constraint. The order is the order a list of them is rendered in — turf
 * first because it is the one a player asks after by name.
 *
 * `glyph` is a single letter for `placeTypeGlyph`'s reason verbatim: it is
 * drawn at ~9px inside a small marker, where anything with internal detail
 * becomes a smudge. `matting` and `concrete` do not collide (M/C), so no
 * value needs a two-letter exception.
 */
export const FACILITY_SURFACES = ["turf", "synthetic", "matting", "concrete"];

/**
 * The facility family's one colour, canonical here since #548 gave it a
 * second map surface (the draft pin in `components/SurfacePinMap.jsx`,
 * beside `GroundsMap.jsx`'s markers). Violet ON PURPOSE: outside
 * `SOURCE_COLORS` (gold/blue/navy — whose ground) and outside the red/teal
 * the pending and device markers use, so no legend entry gets a second
 * meaning. Two files drawing "facility violet" from two literals is the
 * drift `formatKm`'s history (#436) warns about, in a colour.
 */
export const FACILITY_COLOR = "#7C3AED";

/**
 * Issue #544 — what a surface IS, where `FACILITY_SURFACES` says what it is
 * made of: a playing `field` or a practice `net`. Character for character
 * the API's `FACILITY_KINDS` and the `ground_facilities_kind_check` CHECK
 * constraint. `field`, not `pitch`, by Martin's 2026-09-01 call: the stored
 * value matches the word on screen ("Fields & Nets") rather than forcing a
 * translation layer between database and copy.
 *
 * The order is the render order: fields first, then nets — the section
 * header names both, in that order, and the list under it agrees.
 *
 * NULL/absent on a row means NOBODY HAS SAID — pre-#544 history,
 * deliberately un-backfilled. There is no `unknown` member: unknown is the
 * absence of an answer, not a third kind of surface, and every reader
 * renders it as a neutral word rather than a guess.
 */
export const FACILITY_KINDS = ["field", "net"];

export const FACILITY_KIND_META = {
  field: { label: "Field" },
  net: { label: "Net" },
};

/** The kind in words, or `null` when nobody has said. Never a guess:
 * `facilitySurfaceGlyph`'s argument, one column over. */
export function facilityKindLabel(value) {
  return FACILITY_KIND_META[value]?.label ?? null;
}

/**
 * Issue #544's grouping: within the one "Fields & Nets" section, fields
 * first, then nets, then the rows nobody has typed — which is the whole
 * point of a header that names both. Unknown LAST, not hidden and not
 * guessed into a group: a pre-#544 row belongs to the venue as much as a
 * typed one does, it just cannot yet say which half it belongs in.
 *
 * A NEW array (the caller's list is shared state), and a STABLE sort:
 * within each group the incoming order — the API's oldest-first — stands.
 */
export function sortFacilitiesByKind(list) {
  const rank = (f) => {
    const i = FACILITY_KINDS.indexOf(f?.kind);
    return i === -1 ? FACILITY_KINDS.length : i;
  };
  return [...(list || [])].sort((a, b) => rank(a) - rank(b));
}

export const FACILITY_SURFACE_META = {
  turf: { label: "Turf", glyph: "T" },
  synthetic: { label: "Synthetic", glyph: "S" },
  matting: { label: "Matting", glyph: "M" },
  concrete: { label: "Concrete", glyph: "C" },
};

/**
 * FAC-03's five ratings (#165's `FACILITY_CONDITIONS`), best to worst.
 *
 * THIS IS NOW THE WHOLE VENUE FEATURE'S VOCABULARY, not just a facility's.
 * #164/#418 gave `grounds` its own condition rating and deliberately wrote
 * it from the SAME five words (`api/src/lib/conditionRating.ts`, which spells
 * out why two lists one table apart would be unsortable and unfilterable).
 * The client copy has to hold that line too: when the venue rating gets a
 * surface, it reads its labels and tones from here rather than minting a
 * second list, or the app will show "Poor" for a ground and "Needs repair"
 * for its net with no way to compare them.
 */
export const FACILITY_CONDITIONS = ["excellent", "good", "fair", "poor", "needs_repair"];

/**
 * `needs_repair` is stored tokenised and read "Needs repair" — the label is
 * a rendering concern, exactly as the API comment says. `tone` drives the
 * badge colour: the bottom two are the states a player wants to notice
 * before they drive somewhere.
 */
export const FACILITY_CONDITION_META = {
  excellent: { label: "Excellent", tone: "good" },
  good: { label: "Good", tone: "good" },
  fair: { label: "Fair", tone: "fair" },
  poor: { label: "Poor", tone: "bad" },
  needs_repair: { label: "Needs repair", tone: "bad" },
};

export function facilitySurfaceLabel(value) {
  return FACILITY_SURFACE_META[value]?.label ?? null;
}

/**
 * The marker letter. `?` rather than a fallback to turf, on
 * `placeTypeGlyph`'s argument: a surface the client cannot read is not turf,
 * and drawing it as turf puts a claim on the map that no row supports.
 */
export function facilitySurfaceGlyph(value) {
  return FACILITY_SURFACE_META[value]?.glyph ?? "?";
}

/**
 * FAC-06's SECOND clause, on the client: a facility can be SELECTED when
 * scheduling a training session, and the two ids move as a pair.
 *
 * THE RULE. `training_sessions` holds `(ground_id, facility_id)` and the API
 * validates them as a pair — a net of another venue is refused
 * (`facility_not_found`), and a net with no venue is refused twice
 * (`facility_needs_ground`, plus `training_sessions_facility_place_check` in
 * the engine). This function is that rule as the FORM experiences it: change
 * the venue and the surface you picked at the old one is gone, because it is
 * not there.
 *
 * WHY IT IS HERE AND NOT INSIDE THE DIALOG. The server refusing a bad pair is
 * the boundary; a form that can BUILD one and only finds out at save time is
 * a form that wastes a round trip and blames the user for it. It is three
 * lines, and three lines of rule inside JSX is three lines no unit test in
 * this repo can reach (see `groundRef.js`'s header for why that matters).
 *
 * ZERO-TAP CONVERGENCE IS SAFE THROUGH THIS (#356). `GroundPicker` resolves a
 * legacy row's stored NAME to a record on load, which moves `groundId` from
 * `null` to an id. That is a "changed" ground by this predicate and clears
 * the surface — which costs nothing, because a session whose `ground_id` is
 * null cannot have a `facility_id` at all: the CHECK constraint forbids it.
 */
export function facilityAfterGroundChange(previousGroundId, nextGroundId, facilityId) {
  if (!facilityId || !nextGroundId) return null;
  return nextGroundId === previousGroundId ? facilityId : null;
}

/**
 * One surface, named the way a person picks it: "Net 3 · Synthetic".
 *
 * BOTH HALVES, because neither is enough on its own. A venue's rows are
 * routinely called "Net 1".."Net 4", which says nothing about what you will
 * be batting on; and two rows can legitimately share a surface, so the
 * material alone cannot identify one. FAC-06 is "which surface" and FAC-02 is
 * "which net" — this is both, in the order a person says them.
 *
 * A row with no readable surface keeps its name rather than gaining a
 * guessed one, on `facilitySurfaceGlyph`'s argument exactly.
 */
export function facilityOptionLabel(facility) {
  const name = (facility?.name || "").trim();
  const surface = facilitySurfaceLabel(facility?.surface);
  if (!name) return surface || "";
  return surface ? `${name} · ${surface}` : name;
}

/**
 * The stored surface label for an EDITING match's own `facility_name`/
 * `facility_surface` columns (#1028) — the only case `FacilityPicker`'s
 * `storedLabel` prop needs: the venue's current surface list is
 * unreadable (the coach's private ground, `useGroundFacilities`'s own
 * 404-is-empty rule) or has changed since the match was logged, and the
 * picker has to fall back to what was recorded rather than an empty
 * control. `null` on a fresh log and on every match that recorded no
 * surface, which is most of them — `facilityOptionLabel` already returns
 * `""`/the bare name for a row with no surface, so the `facility_name`
 * check here is the ONLY guard: a match logged before the column existed
 * must show nothing, not "· " with no name in front of it.
 */
export function facilityStoredLabel(match) {
  return match?.facility_name
    ? facilityOptionLabel({ name: match.facility_name, surface: match.facility_surface })
    : null;
}

/** The rating in words, or `null` when nobody has judged it. Never a guess:
 * unrated is the ABSENCE of a rating (#165), not a sixth value. */
export function facilityConditionLabel(value) {
  return FACILITY_CONDITION_META[value]?.label ?? null;
}

export function facilityConditionTone(value) {
  return FACILITY_CONDITION_META[value]?.tone ?? "unknown";
}

/** Both coordinates or neither — `ground_facilities_pin_check` guarantees it
 * server-side, and this is the client asking the same question rather than
 * trusting half a pin to be impossible. */
export function hasFacilityPin(f) {
  return !!f && typeof f.latitude === "number" && typeof f.longitude === "number";
}

/**
 * A season. #164's point is that an undated rating is worse than none
 * because stale reads as current, and the API answered it by making
 * `condition_rated_at` travel with every rating. That fixes the DATA; a
 * reader still has to do the arithmetic. Six months is the line because a
 * New Zealand cricket surface is re-laid, re-sown or left under winter grass
 * between seasons — a rating from the other side of that gap describes a
 * surface that no longer exists, whatever it says.
 *
 * The rating is never HIDDEN when it is stale, only marked: "good, last
 * looked at 14 months ago" is more information than silence, and deciding
 * for the reader that an old observation is worthless is not this module's
 * call to make.
 */
export const CONDITION_STALE_DAYS = 180;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days between an ISO timestamp and `now`, or `null` when the string
 * is missing or unparseable. Unparseable is `null` — not `Infinity`, which
 * would render a bad date as maximally stale and a missing one as fresh. */
export function daysSince(iso, now = new Date()) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const ms = now.getTime() - then;
  if (ms < 0) return 0; // A future stamp is a clock disagreement, not news.
  return Math.floor(ms / DAY_MS);
}

/**
 * "Rated today" / "Rated 3 days ago" / "Rated 7 months ago".
 *
 * Months rather than a date because the question a reader is asking is "is
 * this still true", and "7 months ago" answers it where "12 January" makes
 * them do the subtraction. Returns `null` when there is nothing to date —
 * which is exactly when there is no rating either, since the API sends the
 * pair or neither.
 */
export function conditionRatedLabel(iso, now = new Date()) {
  const days = daysSince(iso, now);
  if (days === null) return null;
  if (days === 0) return "Rated today";
  if (days === 1) return "Rated yesterday";
  if (days < 30) return `Rated ${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `Rated ${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.floor(days / 365);
  return `Rated ${years} year${years === 1 ? "" : "s"} ago`;
}

export function isConditionStale(iso, now = new Date()) {
  const days = daysSince(iso, now);
  return days !== null && days >= CONDITION_STALE_DAYS;
}

/**
 * THE CLIENT HALF OF THE TRUST PATH (#166).
 *
 * `GET /api/grounds/facilities` already returns only facilities of grounds
 * this caller may see — the join is the authorisation, and the API suite
 * proves it. This function refuses to draw anything whose ground is not in
 * the directory THIS PAGE IS ALREADY SHOWING, which is a strictly narrower
 * set and a second, independent gate:
 *
 *   - a facility whose parent is missing from the directory response is
 *     dropped rather than drawn at its own coordinates. Drawing it would put
 *     a pin on the map with no ground under it — which is precisely how a
 *     private ground would announce itself, and it would do so at the
 *     facility's OWN coordinates, i.e. the accurate ones.
 *   - it costs nothing, and it means a future widening of either endpoint
 *     cannot silently widen the map: both have to agree before a pin is
 *     drawn.
 *
 * `visibleGroundIds` is a Set of the ids `GET /api/grounds` returned — the
 * DIRECTORY, not the filtered view. Filtering (search, chips, near-me) is
 * applied afterwards, per-ground, by the caller.
 */
export function facilitiesByGround(facilities, visibleGroundIds) {
  const byGround = new Map();
  (facilities || []).forEach((f) => {
    if (!f || !f.ground_id) return;
    if (!visibleGroundIds || !visibleGroundIds.has(f.ground_id)) return;
    const list = byGround.get(f.ground_id);
    if (list) list.push(f);
    else byGround.set(f.ground_id, [f]);
  });
  return byGround;
}

/**
 * The facilities to DRAW for a set of grounds already on the map: each one
 * that carries its own pin, paired with the ground it hangs off so the popup
 * can say where it is without a second lookup.
 *
 * Takes the grounds the map is actually drawing (post-filter), so a facility
 * appears exactly when its venue does. Hiding a ground by search or chip and
 * leaving its nets floating on the map would be both confusing and, for a
 * type filter, wrong.
 */
export function facilityMarkers(groundsOnMap, byGround) {
  const markers = [];
  (groundsOnMap || []).forEach((g) => {
    (byGround.get(g.ground_id) || []).forEach((f) => {
      // #166 FAC-11: an archived surface keeps its row and its listing on
      // the venue (badged), but not a pin of its own — a facility pin
      // exists to send somebody to a net they can train on, and a torn one
      // is not that. The rows are only present at all because this page
      // asks for them (`include_archived=1`); the default map read excludes
      // them server-side.
      if (hasFacilityPin(f) && !f.archived_at) markers.push({ facility: f, ground: g });
    });
  });
  return markers;
}

/**
 * "3 surfaces · Turf, Synthetic" — what a venue is made of, in one line, for
 * a card or a popup.
 *
 * Surfaces are listed distinct and in vocabulary order, not once per row:
 * "turf, turf, turf" is noise where "3 surfaces · Turf" is the fact.
 *
 * ## ISSUE #652 — THE COUNT IS `facility_count`, NOT THE ROWS. Read this
 * before changing either number.
 *
 * It used to be the rows. #643 made that unsafe, and the mechanism is worth
 * stating because nothing about it is visible on screen:
 * `ground_facilities.latitude/longitude` are NULLABLE by design (#137/#544 —
 * a club can register three nets before anyone has stood in front of them
 * with a phone), and SQL comparison against NULL is never true. So the
 * viewport read this page now makes — `GET /api/grounds/facilities?bbox=…` —
 * **cannot return an unpinned surface at all**. Correct for the map, where
 * an unpinned surface has nowhere to be drawn; silently wrong here, where
 * the same rows are counted: a venue with two pinned nets and one unpinned
 * would have read "2 surfaces", plausibly, forever.
 *
 * #652 named three options, and the answer landed in TWO places. The server
 * takes option 2 in the predicate itself — `facilitiesInBoxWhere` now returns
 * an unpinned surface whose VENUE is in the box, which costs no extra query
 * because that read already joins `grounds` — so in the ordinary case the
 * rows here are complete and the breakdown names every surface.
 *
 * What remains, and what the arithmetic below is for, is the case the server
 * cannot fix: the facilities read is CAPPED (`FACILITIES_BBOX_MAX_ROWS`), and
 * a capped answer is short by an unknown amount. So this is **option 1 as the
 * belt to that braces**:
 *
 *   - the COUNT comes from `facility_count`, which `GET /api/grounds` derives
 *     server-side over every row of the venue (#164/#418). It is complete by
 *     construction, unaffected by the box, by the pin being null and by the
 *     row cap — the three things that can shorten the list — and it costs no
 *     extra request because it is already on the ground row this popup is
 *     rendered from.
 *   - the SURFACE NAMES still come from the rows, because only the rows can
 *     say what the surfaces are made of, and losing "turf, synthetic" to buy
 *     a correct integer would be a bad trade on a screen whose whole question
 *     is "what will I be batting on".
 *   - when the rows are FEWER than the count, the line says so: `+1 more`.
 *     Option 3 (accept the under-report, change the copy) was rejected — a
 *     venue popup is about the venue, not about what happens to be pinned —
 *     and option 2 (a second request for the unpinned rows) buys a complete
 *     breakdown for a round trip per viewport, which is the cost this epic
 *     exists to remove. The marker keeps the count honest AND keeps the
 *     partial breakdown honest, which is the pair #612 and #617 both landed
 *     on for the same class of defect.
 *
 * `Math.max` rather than trusting either number blindly: an optimistic local
 * update (#601 rates a net, #548 adds one) can leave the rows ahead of a
 * `facility_count` fetched before it, and the larger of the two is the one
 * with evidence behind it.
 */
export function facilitySummaryLabel(list, completeCount = 0) {
  const rows = (list || []).filter(Boolean);
  const total = Math.max(rows.length, completeCount || 0);
  if (total === 0) return null;
  const noun = total === 1 ? "surface" : "surfaces";
  // No rows at all: the facilities request failed, or this is cell mode, or
  // every one of the venue's surfaces is unpinned. The page knows how many
  // there are and not what they are made of, and saying the true half beats
  // saying nothing and reading as "this venue has none".
  if (rows.length === 0) return `${total} ${noun}`;
  const surfaces = FACILITY_SURFACES.filter((s) => rows.some((f) => f.surface === s)).map(
    (s) => FACILITY_SURFACE_META[s].label,
  );
  if (surfaces.length === 0) return `${total} ${noun}`;
  // #652: the count is complete, the breakdown is not. Never "+0 more", and
  // never a silent difference between the two.
  const missing = total - rows.length;
  const named = missing > 0 ? [...surfaces, `+${missing} more`] : surfaces;
  return `${total} ${noun} · ${named.join(", ")}`;
}

/**
 * The count of facilities across a whole directory view — what the "show
 * facilities" toggle puts on its own label, so pressing it is a decision
 * rather than a guess about whether anything will change.
 */
export function facilityCount(byGround) {
  let n = 0;
  byGround.forEach((list) => {
    n += list.length;
  });
  return n;
}
